import { ActMap, MapNode, NodeType, NodeCombatConfig } from '../types/Map.types';
import { EnemyType } from '../types/Combat.types';

// === GÉNÉRATEUR DE CARTE STYLE SLAY THE SPIRE ===
// Crée un arbre de chemins avec nœuds interconnectés.
// Structure : 1 nœud départ → 6 étages de 2-4 nœuds → 1 boss

const TOTAL_FLOORS = 12; // 0=départ, 1-10=étages normaux, 11=boss final
const MIN_NODES_PER_FLOOR = 2;
const MAX_NODES_PER_FLOOR = 4;

// Pools d'ennemis par acte / difficulté
//
// Les compositions sont *thématiques* pour que chaque acte se joue différemment
// (feedback playtest v0.31.4 : combats post-boss trop répétitifs) :
//   - EASY   → Acte 1 intro : mêlée basique (zombie, brute, crab) — apprendre les déplacements
//   - MEDIUM → Acte 1 fin   : introduction de la pression distance (gunner, bomber, siren)
//   - HARD   → Acte 2       : compositions support / punitives (cursed aura, siren heal, gunner LOS)

// === ACTE 1 INTRO — Mêlée basique (étages 1-2) ===
const EASY_ENCOUNTERS: NodeCombatConfig[] = [
  {
    enemies: [
      { type: 'zombie', col: 5, row: 3 },
      { type: 'zombie', col: 6, row: 5 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'zombie', col: 5, row: 2 },
      { type: 'zombie', col: 4, row: 5 },
      { type: 'zombie', col: 6, row: 4 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'zombie', col: 5, row: 3 },
      { type: 'crab', col: 6, row: 5 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'brute', col: 6, row: 4 },
      { type: 'zombie', col: 5, row: 2 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'fast', col: 5, row: 2 },
      { type: 'fast', col: 6, row: 5 },
      { type: 'zombie', col: 4, row: 4 },
    ],
    isBoss: false,
  },
];

// === ACTE 1 FIN — Pression distance introduite (étages 3-5) ===
const MEDIUM_ENCOUNTERS: NodeCombatConfig[] = [
  // Mêlée renforcée avec un tireur au fond
  {
    enemies: [
      { type: 'gunner', col: 6, row: 1 },
      { type: 'zombie', col: 5, row: 4 },
      { type: 'zombie', col: 6, row: 5 },
    ],
    isBoss: false,
  },
  // Brute + tireur : forcer à choisir la cible prioritaire
  {
    enemies: [
      { type: 'brute', col: 5, row: 3 },
      { type: 'gunner', col: 6, row: 1 },
    ],
    isBoss: false,
  },
  // Bomber à éviter + mobilité ennemie
  {
    enemies: [
      { type: 'bomber', col: 6, row: 3 },
      { type: 'zombie', col: 5, row: 5 },
      { type: 'fast', col: 4, row: 2 },
    ],
    isBoss: false,
  },
  // Crabe qui verrouille + sirène qui soigne derrière
  {
    enemies: [
      { type: 'crab', col: 5, row: 3 },
      { type: 'siren', col: 6, row: 5 },
      { type: 'zombie', col: 4, row: 6 },
    ],
    isBoss: false,
  },
  // Requin rapide qui punit les cibles blessées
  {
    enemies: [
      { type: 'shark', col: 5, row: 2 },
      { type: 'brute', col: 6, row: 5 },
    ],
    isBoss: false,
  },
];

// === ACTE 2 — Compositions support punitives (étages 7+) ===
const HARD_ENCOUNTERS: NodeCombatConfig[] = [
  {
    enemies: [
      { type: 'brute', col: 6, row: 3 },
      { type: 'fast', col: 5, row: 1 },
      { type: 'zombie', col: 5, row: 6 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'shark', col: 5, row: 1 },
      { type: 'gunner', col: 6, row: 6 },
      { type: 'cursed', col: 4, row: 4 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'brute', col: 5, row: 3 },
      { type: 'brute', col: 6, row: 5 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'bomber', col: 5, row: 2 },
      { type: 'bomber', col: 6, row: 5 },
      { type: 'fast', col: 4, row: 4 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'crab', col: 6, row: 3 },
      { type: 'siren', col: 5, row: 1 },
      { type: 'gunner', col: 5, row: 6 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'shark', col: 4, row: 2 },
      { type: 'shark', col: 6, row: 5 },
    ],
    isBoss: false,
  },
  // Composition support punitive : sirène soigne + maudit affaiblit + tireur ligne
  {
    enemies: [
      { type: 'siren', col: 6, row: 1 },
      { type: 'cursed', col: 5, row: 4 },
      { type: 'gunner', col: 6, row: 6 },
    ],
    isBoss: false,
  },
  // Bomber groupé + zombies pour forcer le scatter
  {
    enemies: [
      { type: 'bomber', col: 5, row: 3 },
      { type: 'zombie', col: 6, row: 2 },
      { type: 'zombie', col: 6, row: 5 },
      { type: 'fast', col: 4, row: 4 },
    ],
    isBoss: false,
  },
];

// Combats élite (plus durs, meilleur loot)
const ELITE_ENCOUNTERS: NodeCombatConfig[] = [
  {
    enemies: [
      { type: 'brute', col: 5, row: 3 },
      { type: 'fast', col: 6, row: 1 },
      { type: 'fast', col: 4, row: 5 },
      { type: 'zombie', col: 6, row: 6 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'shark', col: 5, row: 1 },
      { type: 'crab', col: 6, row: 5 },
      { type: 'gunner', col: 4, row: 3 },
      { type: 'siren', col: 6, row: 3 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'cursed', col: 5, row: 2 },
      { type: 'cursed', col: 6, row: 5 },
      { type: 'bomber', col: 4, row: 4 },
    ],
    isBoss: false,
  },
  {
    enemies: [
      { type: 'brute', col: 5, row: 2 },
      { type: 'brute', col: 6, row: 5 },
      { type: 'shark', col: 4, row: 4 },
    ],
    isBoss: false,
  },
];

// 3 boss différents (sélection aléatoire)
const BOSS_ENCOUNTERS: NodeCombatConfig[] = [
  {
    enemies: [
      { type: 'boss', col: 6, row: 4 },
      { type: 'zombie', col: 5, row: 2 },
      { type: 'fast', col: 5, row: 6 },
    ],
    isBoss: true,
  },
  {
    enemies: [
      { type: 'boss_kraken', col: 6, row: 4 },
      { type: 'crab', col: 5, row: 2 },
      { type: 'crab', col: 5, row: 6 },
    ],
    isBoss: true,
  },
  {
    enemies: [
      { type: 'boss_voodoo', col: 6, row: 4 },
      { type: 'cursed', col: 5, row: 2 },
      { type: 'siren', col: 5, row: 6 },
    ],
    isBoss: true,
  },
];

/** Utilitaire : élément aléatoire d'un tableau */
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Génère la config de combat selon le type de nœud et l'étage */
function generateCombatConfig(type: NodeType, floor: number): NodeCombatConfig | undefined {
  if (type === 'rest' || type === 'event' || type === 'shop' || type === 'explore' || type === 'survivor' || type === 'extract') return undefined;
  if (type === 'boss') return pick(BOSS_ENCOUNTERS);
  if (type === 'elite') return pick(ELITE_ENCOUNTERS);

  // Combat normal : difficulté selon l'étage (12 étages)
  if (floor <= 2) return pick(EASY_ENCOUNTERS);
  if (floor <= 5) return pick(MEDIUM_ENCOUNTERS);
  return pick(HARD_ENCOUNTERS);
}

/** Détermine le type de nœud pour un étage donné */
function pickNodeType(floor: number, index: number, nodesInFloor: number): NodeType {
  const roll = Math.random();

  // === ACTE 1 : L'Archipel (étages 1-4) ===

  if (floor === 1) {
    return roll < 0.3 ? 'explore' : 'combat';
  }
  if (floor === 2) {
    if (roll < 0.15) return 'survivor';
    if (roll < 0.35) return 'event';
    if (roll < 0.5) return 'explore';
    return 'combat';
  }
  if (floor === 3) {
    if (roll < 0.2) return 'elite';
    if (roll < 0.4) return 'event';
    if (roll < 0.55) return 'shop';
    if (roll < 0.65) return 'survivor';
    return 'combat';
  }
  // Étage 4 : extraction + repos (fin Acte 1)
  if (floor === 4) {
    if (roll < 0.2) return 'extract';
    if (roll < 0.4) return 'rest';
    if (roll < 0.55) return 'shop';
    if (roll < 0.65) return 'explore';
    return 'combat';
  }

  // === ACTE 2 : Les Récifs Sanglants (étages 5-8) ===

  if (floor === 5) {
    if (roll < 0.25) return 'elite';
    if (roll < 0.4) return 'event';
    if (roll < 0.55) return 'survivor';
    return 'combat';
  }
  // Étage 6 : boss intermédiaire (un des 3 boss au hasard)
  if (floor === 6) {
    if (roll < 0.3) return 'elite';
    if (roll < 0.5) return 'event';
    if (roll < 0.6) return 'shop';
    return 'combat';
  }
  // Étage 7 : extraction + repos (fin Acte 2)
  if (floor === 7) {
    if (roll < 0.2) return 'extract';
    if (roll < 0.4) return 'rest';
    if (roll < 0.55) return 'shop';
    return 'combat';
  }

  // === ACTE 3 : Le Temple Englouti (étages 8-10) ===

  if (floor === 8) {
    if (roll < 0.3) return 'elite';
    if (roll < 0.5) return 'event';
    if (roll < 0.6) return 'survivor';
    return 'combat';
  }
  if (floor === 9) {
    if (roll < 0.35) return 'elite';
    if (roll < 0.5) return 'event';
    return 'combat';
  }
  // Étage 10 : dernière extraction + repos (dernière chance)
  if (floor === 10) {
    if (roll < 0.2) return 'extract';
    if (roll < 0.35) return 'rest';
    if (roll < 0.5) return 'elite';
    return 'combat';
  }

  // Fallback (ne devrait pas arriver)
  return 'combat';
}

/** Génère une carte complète pour un acte */
export function generateActMap(): ActMap {
  const nodes: MapNode[] = [];

  // Étage 0 : nœud de départ (combat facile)
  const startNode: MapNode = {
    id: '0-0',
    floor: 0,
    index: 0,
    type: 'combat',
    status: 'available',
    x: 0.5,
    connections: [],
    combatConfig: pick(EASY_ENCOUNTERS),
  };
  nodes.push(startNode);

  // Étages 1 à TOTAL_FLOORS-2 : nœuds normaux (+ boss intermédiaire étage 6)
  for (let floor = 1; floor < TOTAL_FLOORS - 1; floor++) {
    // Boss intermédiaire à l'étage 6 (fin Acte 2) — nœud unique comme le boss final
    if (floor === 6) {
      const midBossNode: MapNode = {
        id: `${floor}-0`,
        floor,
        index: 0,
        type: 'boss',
        status: 'locked',
        x: 0.5,
        connections: [],
        combatConfig: pick(BOSS_ENCOUNTERS),
      };
      nodes.push(midBossNode);
      continue;
    }

    const count = MIN_NODES_PER_FLOOR + Math.floor(Math.random() * (MAX_NODES_PER_FLOOR - MIN_NODES_PER_FLOOR + 1));

    for (let i = 0; i < count; i++) {
      const type = pickNodeType(floor, i, count);
      const node: MapNode = {
        id: `${floor}-${i}`,
        floor,
        index: i,
        type,
        status: 'locked',
        x: (i + 1) / (count + 1) + (Math.random() - 0.5) * 0.08,
        connections: [],
        combatConfig: generateCombatConfig(type, floor),
      };
      nodes.push(node);
    }
  }

  // Dernier étage : boss
  const bossNode: MapNode = {
    id: `${TOTAL_FLOORS - 1}-0`,
    floor: TOTAL_FLOORS - 1,
    index: 0,
    type: 'boss',
    status: 'locked',
    x: 0.5,
    connections: [],
    combatConfig: pick(BOSS_ENCOUNTERS),
  };
  nodes.push(bossNode);

  // Générer les connexions entre étages
  connectFloors(nodes);

  return {
    nodes,
    currentNodeId: null,
    totalFloors: TOTAL_FLOORS,
  };
}

/** Connecte les nœuds entre étages adjacents */
function connectFloors(nodes: MapNode[]): void {
  const floors = new Map<number, MapNode[]>();
  for (const node of nodes) {
    if (!floors.has(node.floor)) floors.set(node.floor, []);
    floors.get(node.floor)!.push(node);
  }

  const maxFloor = Math.max(...floors.keys());

  for (let f = 0; f < maxFloor; f++) {
    const current = floors.get(f) ?? [];
    const next = floors.get(f + 1) ?? [];
    if (current.length === 0 || next.length === 0) continue;

    // Trier par position x
    current.sort((a, b) => a.x - b.x);
    next.sort((a, b) => a.x - b.x);

    // Chaque nœud du bas se connecte au nœud le plus proche au-dessus
    for (const node of current) {
      let closest = next[0];
      let minDist = Math.abs(node.x - closest.x);
      for (const n of next) {
        const dist = Math.abs(node.x - n.x);
        if (dist < minDist) {
          minDist = dist;
          closest = n;
        }
      }
      if (!node.connections.includes(closest.id)) {
        node.connections.push(closest.id);
      }
    }

    // Chaque nœud du haut doit avoir au moins un parent
    for (const node of next) {
      const hasParent = current.some(c => c.connections.includes(node.id));
      if (!hasParent) {
        // Connecter au parent le plus proche
        let closest = current[0];
        let minDist = Math.abs(node.x - closest.x);
        for (const c of current) {
          const dist = Math.abs(node.x - c.x);
          if (dist < minDist) {
            minDist = dist;
            closest = c;
          }
        }
        if (!closest.connections.includes(node.id)) {
          closest.connections.push(node.id);
        }
      }
    }

    // Connexions supplémentaires aléatoires pour créer des choix
    for (const node of current) {
      for (const n of next) {
        if (!node.connections.includes(n.id) && Math.abs(node.x - n.x) < 0.3 && Math.random() < 0.3) {
          node.connections.push(n.id);
        }
      }
    }
  }
}
